import { useEffect, useState } from "react";
import { DownloadSimple, ChartLineUp, Buildings, FileX, FilePdf } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Input } from "../../components/ui/input";

const today = () => new Date().toISOString().slice(0,7);

export default function MISReport() {
  const { active, activeId, companies } = useCompany();
  const navigate = useNavigate();
  const [month, setMonth] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) { setData(null); return; }
    api.get("/mis/report", { params: { company_id: activeId, month } }).then((r)=>setData(r.data)).catch(()=>{});
  }, [activeId, month]);

  if (companies.length === 0) {
    return (
      <Layout title="Monthly MIS" subtitle="Productivity-driven revenue report">
        <div className="border border-black/10 p-16 text-center bg-white">
          <ChartLineUp size={36} className="mx-auto text-[#D90429]"/>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Start with a company</h3>
          <button onClick={() => navigate("/finance/companies")} className="mt-5 inline-flex items-center gap-2 px-5 py-3 bg-[#D90429] text-white font-semibold hover:bg-[#EF233C]">Go to Companies</button>
        </div>
      </Layout>
    );
  }

  if (!activeId) {
    return <Layout title="Monthly MIS"><div className="p-10 text-center text-[#717680]">Pick a company from the sidebar.</div></Layout>;
  }

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const chartData = rows.map((r) => ({ name: r.entity_name.length > 18 ? r.entity_name.slice(0,16)+"…" : r.entity_name, qc: r.qc_passed_pages, revenue: r.revenue_inr }));

  return (
    <Layout
      title="Monthly MIS"
      subtitle={`${active?.name} · ${month}`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 rounded-none" data-testid="report-month-picker"/>
          <a href={`${API}/mis/report/export?company_id=${activeId}&month=${month}`} target="_blank" rel="noreferrer" data-testid="export-mis-excel" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95">
            <DownloadSimple size={16} weight="bold"/> Export Excel
          </a>
        </>
      }
    >
      {/* KPI band */}
      <div className="grid grid-cols-1 md:grid-cols-5 border border-black/10 bg-white mb-6" data-testid="mis-kpi-band">
        {[
          {label:"Total Revenue", value: formatINR(totals.total_revenue_inr || 0), color:"#2A9D8F", testId:"kpi-revenue"},
          {label:"QC Passed", value: (totals.total_qc_passed || 0).toLocaleString("en-IN"), color:"#0A0A0A", testId:"kpi-qc"},
          {label:"Pages Digitized", value: (totals.total_pages_digitized || 0).toLocaleString("en-IN"), color:"#3A86FF", testId:"kpi-digitized"},
          {label:"Rework", value: (totals.total_rework || 0).toLocaleString("en-IN"), color:"#D90429", testId:"kpi-rework"},
          {label:"Active Entities", value: totals.active_entities || 0, color:"#F4A261", testId:"kpi-entities"},
        ].map((s)=>(
          <div key={s.label} data-testid={s.testId} className="p-5 border-r border-black/5 last:border-r-0">
            <div className="overline" style={{color:s.color}}>{s.label}</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2" style={{color:s.color}}>{s.value}</div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]" data-testid="empty-mis-report">
          <FileX size={36} className="mx-auto text-[#D90429]"/>
          <p className="mt-4">No productivity logged for {month}. Enable MIS billing for clients then start logging daily output.</p>
          <button onClick={()=>navigate("/mis/log")} className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Daily Log</button>
        </div>
      ) : (
        <>
          <div className="border border-black/10 bg-white p-6 mb-6">
            <div className="overline">Revenue by entity</div>
            <h3 className="font-display text-2xl font-bold tracking-tight mb-4">Where this month&rsquo;s revenue is coming from</h3>
            <div style={{ width:"100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#EBECEF" vertical={false}/>
                  <XAxis dataKey="name" stroke="#717680" fontSize={11}/>
                  <YAxis stroke="#717680" fontSize={11} tickFormatter={(v)=>`₹${(v/1000).toFixed(0)}k`}/>
                  <Tooltip cursor={{fill:"#F5F6F8"}} contentStyle={{border:"1px solid #0A0A0A", borderRadius:0}} formatter={(v)=>formatINR(v)}/>
                  <Bar dataKey="revenue" fill="#D90429"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-black/10 bg-white overflow-x-auto">
            <div className="p-5 border-b border-black/10 flex items-center justify-between">
              <div><div className="overline">MIS Breakdown</div><h3 className="font-display text-xl font-bold tracking-tight mt-1">{month} — per-entity report</h3></div>
              <div className="text-xs text-[#717680] font-mono-shelv">{rows.length} active</div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="mis-report-table">
              <thead className="bg-[#F5F6F8] text-left"><tr>{["Entity","Contract","Days","Scanned","Digitized","QC Passed","Rework","Hours","Revenue"].map(h=>(<th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
              <tbody>
                {rows.map((r)=>(
                  <tr key={r.entity_id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`mis-row-${r.entity_id}`}>
                    <td className="px-4 py-3 font-semibold">{r.entity_name}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-mono-shelv uppercase">{r.contract_type.replace("_"," ")}</div>
                      <div className="text-[10px] text-[#717680] font-mono-shelv">{r.contract_type === "per_page" ? `₹${r.rate_per_page}/page` : `₹${r.fixed_monthly_rate}/mo`}</div>
                    </td>
                    <td className="px-4 py-3 font-mono-shelv">{r.log_days}</td>
                    <td className="px-4 py-3 font-mono-shelv">{r.pages_scanned.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 font-mono-shelv">{r.pages_digitized.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 font-mono-shelv text-[#2A9D8F]">{r.qc_passed_pages.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 font-mono-shelv text-[#D90429]">{r.rework_pages.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 font-mono-shelv">{r.hours_worked}</td>
                    <td className="px-4 py-3 font-mono-shelv font-bold text-[#0A0A0A]">{formatINR(r.revenue_inr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#FFE5E9] border-t border-[#D90429]/20">
                <tr>
                  <td colSpan={3} className="px-4 py-3 font-display text-base font-bold">Totals</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold">{(totals.total_pages_scanned || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold">{(totals.total_pages_digitized || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold text-[#2A9D8F]">{(totals.total_qc_passed || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold text-[#D90429]">{(totals.total_rework || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold">{totals.total_hours}</td>
                  <td className="px-4 py-3 font-mono-shelv font-bold text-[#D90429]" data-testid="mis-total-revenue">{formatINR(totals.total_revenue_inr || 0)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
